import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { initialEvaluationForm } from 'pages/new-evaluation-form/evaluationFormSlice';

import {
    getQaEvaluationFormDetail,
    getQaEvaluationFormMultiPackDetail,
} from 'services/qaSpec';
import { CustomNotification } from 'common/components';
import { forwardTo } from 'utils/common/route';

export const queryQaEvalFormKey = (productId, evaluationFormName) => {
    return ['pim', 'evaluation-form', parseInt(productId), evaluationFormName];
};

export const useQueryQaEvaluationForm = ({ productId, evaluationFormName }) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: queryQaEvalFormKey(productId, evaluationFormName),
        queryFn: async () => {
            if (evaluationFormName == 'Multipack') {
                const response = await getQaEvaluationFormMultiPackDetail({
                    productId,
                    evaluationFormName,
                });

                if (!response?.isSuccess) {
                    throw new Error(
                        response?.message ?? 'Something went wrong'
                    );
                }
                return response?.data ?? {};
            } else {
                const response = await getQaEvaluationFormDetail({
                    productId,
                    evaluationFormName,
                });

                return response?.data ?? {};
            }
        },
        refetchOnWindowFocus: false,
        enabled: Boolean(productId) && Boolean(evaluationFormName),
        staleTime: 60 * 1000,
        onError: (err) => {
            CustomNotification.error(err.message ?? 'Something went wrong');
            forwardTo('/product/' + productId);
        },
    });

    useEffect(() => {
        dispatch(
            initialEvaluationForm({
                values: query?.data,
                type: evaluationFormName,
            })
        );
    }, [query?.data, dispatch, evaluationFormName]);

    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                queryKey: queryQaEvalFormKey(productId, evaluationFormName),
            });
        };
    }, [queryClient, productId, evaluationFormName]);

    return query;
};
